export class ToasterService {
  constructor() {}

  show(
    message: string,
    type: 'success' | 'error' = 'success',
    duration: number = 3000
  ): void {
    // Supprimer les toasts existants
    this.hideAll();

    // Créer le toast
    const toast = document.createElement('div');
    toast.className = `toast-notification toast-notification--${type}`;
    toast.innerHTML = `
      <div class="toast-notification__icon">
        <img src="${window.location.origin}/wp-content/themes/cotelub-intranet/dist/assets/icons/share.svg" alt="Partager">
      </div>
      <div class="toast-notification__message">${message}</div>
      <button class="toast-notification__close" type="button">
        <img src="${window.location.origin}/wp-content/themes/cotelub-intranet/dist/assets/icons/close.svg" alt="Fermer">
      </button>
    `;

    // Ajouter les styles CSS
    this.addStyles();

    // Ajouter au DOM
    document.body.appendChild(toast);

    // Animer l'apparition
    requestAnimationFrame(() => {
      toast.classList.add('toast-notification--show');
    });

    // Gérer la fermeture
    const closeButton = toast.querySelector('.toast-notification__close');
    closeButton?.addEventListener('click', () => {
      this.hide(toast);
    });

    // Auto-fermeture
    setTimeout(() => {
      this.hide(toast);
    }, duration);
  }

  hide(toast: HTMLElement): void {
    toast.classList.add('toast-notification--hide');
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, 300);
  }

  hideAll(): void {
    const existingToasts = document.querySelectorAll('.toast-notification');
    existingToasts.forEach((toast) => toast.remove());
  }

  /**
   * Affiche une notification Firebase en haut à droite
   */
  showFirebaseNotification(
    title: string,
    message: string,
    icon: string = '🔔',
    data?: any,
    duration: number = 8000
  ): void {
    // Supprimer les toasts Firebase existants
    const existingToasts = document.querySelectorAll(
      '.firebase-notification-toast'
    );
    existingToasts.forEach((toast) => toast.remove());

    // Créer le toast
    const toast = document.createElement('div');
    toast.className =
      'firebase-notification-toast firebase-notification-toast--show';

    toast.innerHTML = `
      <div class="firebase-notification-toast__content">
        <div class="firebase-notification-toast__icon">${icon}</div>
        <div class="firebase-notification-toast__text">
          <div class="firebase-notification-toast__title">${title}</div>
          <div class="firebase-notification-toast__message">${message}</div>
        </div>
        <button class="firebase-notification-toast__close" type="button">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    `;

    // Ajouter les styles CSS pour le toaster Firebase
    this.addFirebaseNotificationStyles();

    // Ajouter au DOM
    document.body.appendChild(toast);

    // Gérer la fermeture
    const closeButton = toast.querySelector(
      '.firebase-notification-toast__close'
    );
    closeButton?.addEventListener('click', () => {
      this.hideFirebaseNotificationToast(toast);
    });

    // Gérer le clic sur le toast pour rediriger
    toast.addEventListener('click', (e: MouseEvent) => {
      if (
        e.target !== closeButton &&
        !closeButton?.contains(e.target as Node)
      ) {
        this.handleNotificationClick(data);
        this.hideFirebaseNotificationToast(toast);
      }
    });

    // Auto-fermeture après la durée spécifiée
    setTimeout(() => {
      this.hideFirebaseNotificationToast(toast);
    }, duration);
  }

  /**
   * Masque un toaster de notification Firebase
   */
  private hideFirebaseNotificationToast(
    toast: HTMLElement,
    duration: number = 300
  ): void {
    toast.classList.add('firebase-notification-toast--hide');
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, duration);
  }

  /**
   * Gère le clic sur une notification pour rediriger
   */
  private handleNotificationClick(data?: any): void {
    if (!data) return;

    let url = '/';

    if (data.post_type === 'document') {
      url = data.url;
    } else if (data.post_type === 'evenement') {
      url = '/agenda/';
    } else if (data.post_id) {
      url = `/post/${data.post_id}/`;
    } else if (data.url) {
      url = data.url;
    }

    // Rediriger vers la page appropriée
    window.location.href = url;
  }

  /**
   * Ajoute les styles CSS pour les toasters de notification Firebase
   */
  private addFirebaseNotificationStyles(): void {
    // Vérifier si les styles sont déjà ajoutés
    if (document.querySelector('#firebase-notification-styles')) return;

    const style = document.createElement('style');
    style.id = 'firebase-notification-styles';
    style.textContent = `
      .firebase-notification-toast {
        position: fixed;
        top: 20px;
        right: 20px;
        background-color: #ffffff;
        border: 1px solid #e1e5e9;
        border-radius: 12px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
        z-index: 10001;
        transform: translateX(100%);
        opacity: 0;
        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        max-width: 400px;
        min-width: 320px;
        cursor: pointer;
        overflow: hidden;
      }

      .firebase-notification-toast--show {
        transform: translateX(0);
        opacity: 1;
      }

      .firebase-notification-toast--hide {
        transform: translateX(100%);
        opacity: 0;
      }

      .firebase-notification-toast__content {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 16px;
      }

      .firebase-notification-toast__icon {
        font-size: 24px;
        flex-shrink: 0;
        margin-top: 2px;
      }

      .firebase-notification-toast__text {
        flex: 1;
        min-width: 0;
      }

      .firebase-notification-toast__title {
        font-size: 16px;
        font-weight: 600;
        color: #1a1a1a;
        margin-bottom: 4px;
        line-height: 1.3;
      }

      .firebase-notification-toast__message {
        font-size: 14px;
        color: #666666;
        line-height: 1.4;
        word-wrap: break-word;
      }

      .firebase-notification-toast__close {
        background: none;
        border: none;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        padding: 4px;
        border-radius: 6px;
        transition: background-color 0.2s ease;
        color: #999999;
        flex-shrink: 0;
      }

      .firebase-notification-toast__close:hover {
        background-color: #f5f5f5;
        color: #666666;
      }

      .firebase-notification-toast:hover {
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
        transform: translateY(-2px);
      }

      .firebase-notification-toast--hide:hover {
        transform: translateX(100%);
      }

      /* Animation d'entrée */
      @keyframes slideInFromRight {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }

      .firebase-notification-toast--show {
        animation: slideInFromRight 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      }
    `;

    document.head.appendChild(style);
  }

  private addStyles(): void {
    // Vérifier si les styles sont déjà ajoutés
    if (document.querySelector('#toast-styles')) return;

    const style = document.createElement('style');
    style.id = 'toast-styles';
    style.textContent = `
      .toast-notification {
        position: fixed;
        bottom: 20px;
        right: 50%;
        transform: translateX(50%);
        background-color: #404c23;
        color: white;
        padding: 12px 16px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        gap: 12px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        z-index: 10000;
        transform: translateX(100%);
        opacity: 0;
        transition: all 0.3s ease;
        max-width: 400px;
        min-width: 300px;
      }

      .toast-notification--show {
        transform: translateX(50%);
        opacity: 1;
      }

      .toast-notification--hide {
        transform: translateX(100%);
        opacity: 0;
      }

      .toast-notification--error {
        background-color: #dc3545;
      }

      .toast-notification__icon img {
        width: 20px;
        height: 20px;
        filter: brightness(0) invert(1);
      }

      .toast-notification__message {
        flex: 1;
        font-size: 14px;
        font-weight: 500;
      }

      .toast-notification__close {
        background: none;
        border: none;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        transition: background-color 0.2s ease;
      }

      .toast-notification__close:hover {
        background-color: rgba(255, 255, 255, 0.2);
      }

      .toast-notification__close img {
        width: 16px;
        height: 16px;
        filter: brightness(0) invert(1);
      }
    `;

    document.head.appendChild(style);
  }
}

export const toasterService = new ToasterService();

// Rendre disponible globalement
(window as any).toasterService = toasterService;
